<template>
  <!-- Arrière-plan moderne avec gradient comme les autres pages -->
  <div
    class="min-h-screen w-full bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 fixed inset-0 overflow-y-auto pt-16">
    <!-- Indicateur de chargement modernisé -->
    <div v-if="isLoading" class="fixed top-20 right-4 z-50">
      <div
        class="bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-gray-200/50 flex items-center gap-2">
        <div class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span class="text-sm font-medium text-gray-700">Chargement...</span>
      </div>
    </div>

    <!-- Container principal compact -->
    <div class="container mx-auto px-4 py-3 pb-3 h-full flex flex-col min-h-[calc(100vh-4rem)]">

      <!-- Breadcrumbs compacts -->
      <div class="mb-2 flex-shrink-0">
        <Breadcrumbs :crumbs="breadcrumbs" />
      </div>

      <!-- Toolbar compacte en 2 lignes -->
      <div
        class="relative overflow-hidden bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 mb-2 flex-shrink-0">
        <div class="absolute inset-0 bg-gradient-to-r from-blue-600/2 to-indigo-600/2"></div>
        <div class="relative p-2">

          <!-- LIGNE 1 : Titre, Schéma, Modes et Sauvegarde -->
          <div class="flex items-center justify-between gap-4 mb-2">
            <!-- Titre + Schéma -->
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <!-- Icône de formulaire -->
              <div
                class="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileEditIcon class="w-4 h-4 text-white" />
              </div>

              <!-- Input titre -->
              <input v-model="formData.title" type="text"
                :placeholder="currentMode === 'useModel' ? 'Nouveau MEMoS basé sur un modèle' : 'Saisissez un titre...'"
                :disabled="!formData.schema_model_uuid || isReadOnlyMode" :readonly="isReadOnlyMode"
                class="flex-1 px-3 py-2 bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-md text-sm font-medium placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 min-w-0"
                :class="{
                  'opacity-50 cursor-not-allowed': !formData.schema_model_uuid || isReadOnlyMode,
                  'bg-gray-100': isReadOnlyMode
                }" />

              <!-- Badge schéma -->
              <div
                class="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-blue-100/80 backdrop-blur-sm border border-blue-200/50 rounded-md flex-shrink-0">
                <div class="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
                <span class="text-xs font-semibold text-blue-900">{{ formData.selectedSchema?.type || 'Type' }}</span>
                <span class="text-xs text-blue-600">{{ formData.selectedSchema?.appellation || 'Schéma' }}</span>
              </div>


            </div>

            <!-- Modes + Bouton Sauvegarde -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <!-- Modes -->
              <div class="flex items-center gap-2">
                <button :disabled="currentMode === 'useModel' || !formData.schema_model_uuid || isReadOnlyMode"
                  class="relative group flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 transform hover:scale-105"
                  :class="[
                    currentMode === 'useModel' || !formData.schema_model_uuid
                      ? 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                      : formData.is_model
                        ? 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 border border-purple-200 shadow-sm'
                        : 'bg-white/80 backdrop-blur-sm text-gray-600 border border-gray-200/50 hover:bg-gray-50/80 shadow-sm',
                    isReadOnlyMode ? 'cursor-not-allowed' : ''
                  ]" @click="toggleModelMode">
                  <BookTemplateIcon class="h-3 w-3" />
                  <span>Modèle</span>
                  <div v-if="formData.is_model"
                    class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full">
                  </div>
                </button>

                <button
                  :disabled="formData.is_model || currentMode === 'useModel' || !formData.schema_model_uuid || isReadOnlyMode"
                  class="relative group flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 transform hover:scale-105"
                  :class="[
                    formData.is_model || currentMode === 'useModel' || !formData.schema_model_uuid
                      ? 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                      : formData.is_draft
                        ? 'bg-gradient-to-r from-amber-100 to-amber-50 text-amber-700 border border-amber-200 shadow-sm'
                        : 'bg-white/80 backdrop-blur-sm text-gray-600 border border-gray-200/50 hover:bg-gray-50/80 shadow-sm',
                    isReadOnlyMode ? 'cursor-not-allowed' : ''
                  ]" @click="toggleDraftMode">
                  <FileEditIcon class="h-3 w-3" />
                  <span>Brouillon</span>
                  <div v-if="formData.is_draft"
                    class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full">
                  </div>
                </button>

                <button :disabled="currentMode === 'useModel' || !formData.schema_model_uuid || isReadOnlyMode"
                  class="relative group flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-300 transform hover:scale-105"
                  :class="[
                    currentMode === 'useModel' || !formData.schema_model_uuid
                      ? 'bg-gray-200/80 text-gray-400 cursor-not-allowed'
                      : formData.is_public
                        ? 'bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm'
                        : 'bg-white/80 backdrop-blur-sm text-gray-600 border border-gray-200/50 hover:bg-gray-50/80 shadow-sm',
                    isReadOnlyMode ? 'cursor-not-allowed' : ''
                  ]" @click="togglePublicMode">
                  <EyeIcon v-if="formData.is_public" class="h-3 w-3" />
                  <EyeOffIcon v-else class="h-3 w-3" />
                  <span>{{ formData.is_public ? 'Public' : 'Privé' }}</span>
                  <div v-if="formData.is_public"
                    class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full">
                  </div>
                </button>
              </div>

              <!-- Bouton d'enregistrement -->
              <button
                class="group flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all duration-300 transform shadow-md hover:shadow-lg disabled:opacity-50 disabled:transform-none"
                :class="isReadOnlyMode
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:scale-105'"
                :disabled="isSaving || !formData.schema_model_uuid || isReadOnlyMode" @click="handleSaveClick">
                <SaveIcon v-if="!isSaving && !isReadOnlyMode" class="h-4 w-4" />
                <EyeIcon v-else-if="isReadOnlyMode" class="h-4 w-4" />
                <div v-else class="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{{
                  isReadOnlyMode ? 'Consultation' :
                    isSaving ? 'Sauvegarde...' :
                      'Enregistrer'
                }}</span>
                <div v-if="hasChanges && !isSaving && !isReadOnlyMode"
                  class="w-2 h-2 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full animate-pulse"></div>
              </button>
            </div>
          </div>

          <!-- LIGNE 2 : Navigation Étapes + Progress -->
          <div class="flex items-center justify-between gap-4 px-3 py-2 bg-gray-50/70 rounded-lg">
            <!-- Étapes navigation -->
            <div class="flex items-center gap-2 flex-1 overflow-x-auto overflow-y-hidden">
              <template v-for="(step, index) in steps" :key="index">
                <button
                  class="group relative flex items-center gap-2 px-2 py-2 rounded-md text-sm font-medium transition-all duration-300 text-center transform hover:scale-105 w-full"
                  :class="[
                    index === currentStep
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md'
                      : step.visited && step.isComplete
                        ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-700 hover:from-emerald-100 hover:to-emerald-150 border border-emerald-200/50'
                        : step.visited && !step.isComplete
                          ? 'bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 hover:from-amber-100 hover:to-amber-150 border border-amber-200/50'
                          : 'bg-white/80 backdrop-blur-sm text-gray-500 hover:bg-gray-50/80 border border-gray-200/50'
                  ]" :disabled="isNavigationDisabled" @click="goToStep(index)">
                  <!-- Numéro/Icône -->
                  <div class="flex items-center justify-center w-6 h-6 rounded-md text-sm font-bold" :class="[
                    index === currentStep
                      ? 'bg-white/20 text-white'
                      : step.visited && step.isComplete
                        ? 'bg-emerald-500 text-white'
                        : step.visited && !step.isComplete
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                  ]">
                    <CheckIcon v-if="step.visited && step.isComplete && index !== currentStep" class="h-3 w-3" />
                    <span v-else class="text-sm">{{ index + 1 }}</span>
                  </div>

                  <!-- Nom de l'étape -->
                  <span class="hidden sm:block text-sm font-medium whitespace-nowrap">{{ step.name }}</span>
                </button>
              </template>
            </div>

            <!-- Progress circulaire -->
            <div class="relative w-12 h-12 flex-shrink-0">
              <svg class="w-12 h-12 transform -rotate-90" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="18" stroke="currentColor" stroke-width="3" fill="none"
                  class="text-gray-200" />
                <circle cx="24" cy="24" r="18" stroke="currentColor" stroke-width="3" fill="none"
                  class="text-blue-600 transition-all duration-500"
                  :stroke-dasharray="`${(completedStepsCountNoPreview / stepsWithoutPreview.length) * 113.1} 113.1`" />
              </svg>
              <div class="absolute inset-0 flex items-center justify-center">
                <span class="text-[10px] font-bold text-blue-600">{{ Math.round((completedStepsCountNoPreview / stepsWithoutPreview.length) * 100)
                }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contenu de l'étape - Zone principale modernisée -->
      <div class="relative bg-white/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200/50 flex-1 min-h-0"
        :class="currentStep === 5 ? 'overflow-visible' : 'overflow-hidden'">
        <div class="absolute inset-0 bg-gradient-to-br from-gray-50/30 to-blue-50/30"></div>
        <div class="relative h-full" :class="currentStep === 5 ? 'overflow-visible' : 'overflow-y-auto'">
          <component :is="steps[currentStep].component" v-bind="componentProps" class="p-6 h-full"
            @update:model-value="updateFormData" @is-valid="updateStepCompletion"
            @schema-selected="handleSchemaSelected" @schema-type-selected="handleSchemaTypeSelected" @save="saveDraft">
          </component>
        </div>
      </div>

      <!-- Boutons de navigation flottants modernisés -->
      <div v-if="showNextButton && currentStep > 0 && currentStep < steps.length && !isReadOnlyMode"
        class="fixed bottom-6 right-6 z-40 flex gap-3">
        <button
          class="group flex items-center gap-2 px-4 py-3 bg-white/90 backdrop-blur-sm border border-gray-200/50 text-gray-700 rounded-xl text-sm font-medium hover:bg-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 transform hover:scale-105"
          :disabled="isNavigationDisabled" @click="previousStep">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Précédent</span>
        </button>

        <button v-if="currentStep < steps.length - 1"
          class="group flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-medium hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-300 transform hover:scale-105"
          :disabled="isNavigationDisabled" @click="nextStep">
          <span>Suivant</span>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

    </div> <!-- Fin du container principal -->

    <!-- Modal de récupération de schéma -->
    <SchemaRecoveryModal :show="showSchemaRecoveryModal" :missing-schema="missingSchemaInfo || {
      originalUuid: '',
      originalType: '',
      originalName: '',
      memoTitle: ''
    }" :available-schemas="allSchemasList" @schema-replaced="handleSchemaReplaced" @cancel="handleSchemaRecoveryCancel"
      @close="showSchemaRecoveryModal = false" />

    <!-- Notification -->
    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { useSchemasStore } from '@/stores/schemasStore';
import type { NotificationType } from '@/types/notification';
import { BookTemplateIcon, CheckIcon, EyeIcon, EyeOffIcon, FileEditIcon, SaveIcon } from "lucide-vue-next";
import { computed, markRaw, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { memos } from "../api";
import { createMemo, updateMemo } from "../api/memos";
import { useFormMode } from "../composables/useFormMode";
import { useTriid } from "../composables/useTriid";
import { generateUUID } from "../utils/commonUtils";
import Notification from './Notification.vue';
// Imports pour le système de cache
import { useAssetsCacheStore } from '@/stores/assetsCache';
import Breadcrumbs from './Breadcrumbs.vue';
import DataCollectionStep from './form-steps/DataCollectionStep.vue';
import SchemaStep from './form-steps/SchemaStep.vue';
import WhatStep from './form-steps/WhatStep.vue';
import WhenStep from './form-steps/WhenStep.vue';
import WhereStep from './form-steps/WhereStep.vue';
import WhoStep from './form-steps/WhoStep.vue';
import WhyStep from './form-steps/WhyStep.vue';

// AJOUT Aperçu
import MemoPreviewStep from './form-steps/MemoPreviewStep.vue';

import type { Entity } from '@/api/types/core/common';
import type { MemoDataEntry } from '@/api/types/memos/entries';
import { useAuthStore } from '@/stores/auth';
import type { SchemaWithSectionList } from '@/stores/schemasStore';
import type { AgentWithRoles, MemoFormData } from '@/types/memoFormData.d.ts';
import type { FieldConfigCreate } from '../api/field_config';
import type { Section } from '../api/types/core/sections';
import type { MemoCreate } from '../api/types/memos/types';
import SchemaRecoveryModal from './modals/SchemaRecoveryModal.vue';

const props = defineProps<{
  mode?: 'create' | 'edit' | 'useModel';
  memoId?: string;
  initialStep?: number;
}>();

const { generateTriid } = useTriid();
const { currentMode, changeMode } = useFormMode();
const authStore = useAuthStore();
const schemasStore = useSchemasStore();

// Initialisation du système de cache
const assetsCacheStore = useAssetsCacheStore();

const route = useRoute();
const notification = ref<InstanceType<typeof Notification> | null>(null);
const isLoading = ref(true);
const hasChanges = ref(false);
const allSchemasList = ref<SchemaWithSectionList[]>([]);

// État pour le modal de récupération de schéma
const showSchemaRecoveryModal = ref(false);
const missingSchemaInfo = ref<{
  originalUuid: string;
  originalType: string;
  originalName: string;
  memoTitle: string;
} | null>(null);

// Récupérer les paramètres de l'URL si présents
const routeMemoId = route.query.memoId as string;
const routeStep = parseInt(route.query.step as string) || 0;
const routeMode = route.query.mode as string;

// Utiliser les props ou les paramètres de l'URL
const targetMemoId = computed(() => props.memoId || routeMemoId);
const targetStep = computed(() => props.initialStep || routeStep || 0);

// Support du mode import
const isImportMode = computed(() => routeMode === 'import');
const isExistingImport = ref(false);

// Modifier l'étape courante pour démarrer au bon endroit
const currentStep = ref(targetStep.value);
const showNextButton = ref(true);
const isSaving = ref(false);
const fieldConfigs = ref<FieldConfigCreate[]>([]);
const allSections = ref<Section[]>([]);
const selectedSchemaType = ref<string | null>(null);

// Charger toutes les données via le store optimisé
const loadInitialData = async (): Promise<void> => {
  try {
    // OPTIMISATION : Ne charger que si nécessaire
    const needSchemas = !schemasStore.schemas.length;
    const needSections = !schemasStore.sections.length;
    const needFieldConfigs = !schemasStore.fieldConfigs.length;
    // Si au moins un des stores est vide, on recharge tout (pour garder la logique groupée)
    if (needSchemas || needSections || needFieldConfigs) {
      await schemasStore.initializeSchemasData();
    }
    // Récupérer les données du store
    fieldConfigs.value = schemasStore.fieldConfigs.map((c): FieldConfigCreate => ({
      reference_id: c.reference_id,
      condition_id: c.condition_id ?? 0,
      condition_value: c.condition_value ?? ''
    }));
    allSections.value = schemasStore.sections.map(s => ({
      ...s,
      indentationLevel: undefined
    } as Section));
    allSchemasList.value = schemasStore.schemas;
  } catch (error) {
    console.error('Erreur lors du chargement des données initiales:', error);
    if (notification.value) {
      notification.value.showNotification(
        'Erreur lors du chargement des données initiales',
        'error'
      );
    }
  }
};

// Charger les données importées depuis sessionStorage
const loadImportedData = async (): Promise<void> => {
  try {
    const importedDataStr = sessionStorage.getItem('importedMemoData');
    if (importedDataStr) {
      const importedData = JSON.parse(importedDataStr);
      sessionStorage.removeItem('importedMemoData'); // Nettoyer immédiatement

      // Importer directement les données sans vérification d'existence
      Object.assign(formData, importedData);
      
      // Gestion spéciale du schéma pour l'import
      if (formData.schema_model_uuid) {
        // Chercher le schéma dans la liste des schémas disponibles
        let matchingSchema = allSchemasList.value.find(s => s.uuid === formData.schema_model_uuid) as { uuid: string; section_list: number[]; type?: string; appellation?: string; description?: string } | undefined;

        if (matchingSchema) {
          // Schéma trouvé, mettre à jour les données
          if (!Array.isArray(matchingSchema.section_list)) matchingSchema.section_list = [];
          if (!('appellation' in matchingSchema)) matchingSchema.appellation = '';
          if (!('description' in matchingSchema)) matchingSchema.description = '';
          formData.selectedSchema = matchingSchema;
          selectedSchemaType.value = matchingSchema.type || null;
          console.log('✅ Schéma importé trouvé dans le store:', matchingSchema);
        } else {
          // Schéma non trouvé, utiliser les données importées
          console.warn('❌ Schéma non trouvé dans le store, utilisation des données importées');
          if (formData.selectedSchema) {
            selectedSchemaType.value = formData.selectedSchema.type || null;
          }
        }
      }
      
      changeMode('create');
      isExistingImport.value = false;
      if (notification.value) {
        notification.value.showNotification(
          "Nouveau MEMoS importé, prêt pour la création.",
          "success"
        );
      }
      // Marquer l'étape Schéma comme visitée et complète si un schéma est défini
      steps.value[0].visited = true;
      steps.value[0].isComplete = !!formData.schema_model_uuid;
    }
  } catch (error) {
    console.error('Erreur lors du chargement des données importées:', error);
    if (notification.value) {
      notification.value.showNotification(
        'Erreur critique lors du chargement des données importées',
        'error'
      );
    }
  }
};

// Charger les données et/ou le MEMoS au montage si nécessaire
onMounted(async () => {
  try {
    await loadInitialData();
    // Précharger tous les assets en parallèle pour améliorer les performances
    await assetsCacheStore.preloadAllAssets();

    //console log et erreurs avec mcp browser tools
    console.log('DEBUG FORM MOUNTED:', {
      targetMemoId: targetMemoId.value,
      currentMode: currentMode.value,
      isImportMode: isImportMode.value,
      isReadOnlyMode: isReadOnlyMode.value
    });
    // Vérifier s'il y a des données importées à charger
    if (isImportMode.value) {
      await loadImportedData();
    } 
    else if (targetMemoId.value) {
      await loadInitialMemo();

      // Afficher une notification si en mode lecture seule
      if (isReadOnlyMode.value && notification.value) {
        notification.value.showNotification(
          "Vous consultez ce MEMoS en lecture seule car vous n'en êtes pas le propriétaire",
          'info'
        );
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement initial:', error);
    if (notification.value) {
      notification.value.showNotification(
        'Erreur lors du chargement initial',
        'error'
      );
    }
  } finally {
    isLoading.value = false;
  }
});

// Définition des étapes - RÉACTIF avec markRaw pour les composants
const steps = ref([
  { name: 'Schéma', component: markRaw(SchemaStep), visited: false, isComplete: false },
  { name: 'WHAT', component: markRaw(WhatStep), visited: false, isComplete: false },
  { name: 'WHY', component: markRaw(WhyStep), visited: false, isComplete: false },
  { name: 'WHO', component: markRaw(WhoStep), visited: false, isComplete: false },
  { name: 'WHERE', component: markRaw(WhereStep), visited: false, isComplete: false },
  { name: 'WHEN', component: markRaw(WhenStep), visited: false, isComplete: false },
  { name: 'HOW | WHICH', component: markRaw(DataCollectionStep), visited: false, isComplete: false },
  // AJOUT Aperçu
  { name: 'Aperçu', component: markRaw(MemoPreviewStep), visited: false, isComplete: false }
]);

// État du formulaire
const formData = reactive<MemoFormData>({
  uuid: generateUUID(),
  triid: '',
  title: '',
  schema_model_uuid: '',
  selectedSchema: null,
  material_entity_uuid: '',
  digital_entity_input_id: '',
  digital_entity_output_id: '',
  location_uuid: '',
  selectedAgents: [],
  is_model: false,
  is_public: true,
  is_draft: true,
  user_id: authStore.user?.uuid || '',
  project_uuid: '',
  instrumentation_description: '',
  instrumentation_link: '',
  start_time: '',
  end_time: '',
  time_zone: '',
  time_link: '',
  is_saved: false,
  data_entries: [],
  NewDigitalEntityCreated: false,
  formSections: {},
  materialEntity: null,
  digitalEntityInput: null,
  digitalEntityOutput: null,
  location: null,
  project: null
});

// Computed pour vérifier les permissions
const canEditMemo = computed((): boolean => {
  // Si pas de mémo chargé ou en mode création, on peut éditer
  if (!targetMemoId.value || currentMode.value === 'create' || currentMode.value === 'useModel') {
    return true;
  }

  return authStore.canEditAsset({ user_id: formData.user_id });
});

const isReadOnlyMode = computed((): boolean => {
  return !canEditMemo.value && currentMode.value === 'edit';
});

// Configuration pour le mode lecture seule
const readOnlyConfig = computed(() => ({
  isReadOnly: isReadOnlyMode.value,
  allowNavigation: true, // Permettre consultation de toutes les étapes
  showEditHints: true,   // Afficher pourquoi c'est readonly
  restrictedFields: []   // Tous les champs par défaut
}));

// Props à passer au composant actif
const componentProps = computed(() => {
  // Props de base pour tous les composants
  const baseProps = {
    modelValue: formData,
    userId: formData.user_id,
    selectedSchemaType: selectedSchemaType.value || null,
    isSaving: isSaving.value,
    readOnlyConfig: readOnlyConfig.value, // Nouveau prop pour le mode lecture seule
    // Nouveaux props pour le système de cache
    assetsCacheStore,
    canEditAsset: (asset: Entity): boolean => {
      return authStore.isAdmin || asset.user_id === formData.user_id;
    },
    isPublicAsset: (asset: Entity): boolean => {
      return asset.is_public && asset.user_id !== formData.user_id;
    },
    isMyAsset: (asset: Entity): boolean => {
      return asset.user_id === formData.user_id;
    }
  };

  // Step 6 : DataCollectionStep
  if (currentStep.value === 6) {
    return {
      modelValue: formData,
      readOnlyConfig: readOnlyConfig.value
    };
  }
  // Step 7 : Aperçu
  if (currentStep.value === 7) {
    return {
      modelValue: formData,
      readOnlyConfig: readOnlyConfig.value
    };
  }
  // Autres steps
  return {
    ...baseProps,
    fieldConfigs: fieldConfigs.value,
    sections: allSections.value
  };
});

// Méthodes de navigation
const nextStep = (): void => {
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value++;
  }
};

const previousStep = (): void => {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
};

// Mise à jour des données du formulaire
const updateFormData = (newData: Partial<MemoFormData>): void => {
  Object.assign(formData, newData);
};

// Mise à jour de l'état de complétion de l'étape
const updateStepCompletion = (value: boolean): void => {
  if (currentStep.value >= 0) {
    steps.value[currentStep.value].visited = true;
    steps.value[currentStep.value].isComplete = value;
  }
};

const goToStep = async (index: number): Promise<void> => {
  if (currentStep.value !== index) {
    await handleStepChange(index);
  }
};


const handleSchemaSelected = (): void => {
  showNextButton.value = false;
  // On passe à l'étape suivante après un court délai
  setTimeout(() => {
    nextStep();
    setTimeout(() => {
      showNextButton.value = true;
    }, 150); // Réapparition du bouton après la transition de fade
  }, 150); // Délai pour le changement d'étape
};

const handleSchemaTypeSelected = (type: string): void => {
  selectedSchemaType.value = type;
};

// Computed property pour les breadcrumbs
const breadcrumbs = computed(() => {
  const base = { name: 'Outils', path: '/dashboard' };
  const current = {
    name: currentMode.value === 'create' ? 'Créer MEMoS' :
      currentMode.value === 'edit' ? 'Modifier MEMoS' :
        currentMode.value === 'useModel' ? 'Créer Modèle' : 'Formulaire',
    path: route.fullPath
  };
  return [base, current];
});

// Computed property to determine if navigation buttons should be disabled
const isNavigationDisabled = computed(() => {
  return !formData.schema_model_uuid;
});

// Exclure l'étape Aperçu du calcul de complétion
const stepsWithoutPreview = computed(() => steps.value.slice(0, -1));
const completedStepsCountNoPreview = computed(() => {
  return stepsWithoutPreview.value.filter(step => step.isComplete).length;
});

// Fonction pour charger le MEMoS initial
const loadInitialMemo = async (): Promise<void> => {
  if (!targetMemoId.value) return;
  try {
    const response = await memos.fetchMemosUuid(targetMemoId.value);
    if (response && response.length > 0) {
      const initialMemo = response[0];

      // Fonction pour trouver le schéma correspondant
      if (!initialMemo.schema_model) {
        console.warn('Schéma manquant dans le MEMoS:', initialMemo.uuid);
        missingSchemaInfo.value = {
          originalUuid: '',
          originalType: 'Inconnu',
          originalName: 'Schéma sans nom',
          memoTitle: initialMemo.title || 'MEMoS sans titre'
        };
        showSchemaRecoveryModal.value = true;
        return; // Arrêter le chargement jusqu'à ce que l'utilisateur choisisse un remplacement
      }

      let matchingSchema = allSchemasList.value.find(s => s.uuid === initialMemo.schema_model.uuid) as { uuid: string; section_list: number[]; type?: string; appellation?: string; description?: string } | undefined;

      if (matchingSchema) {
        if (!Array.isArray(matchingSchema.section_list)) matchingSchema.section_list = [];
        if (!('appellation' in matchingSchema)) matchingSchema.appellation = '';
        if (!('description' in matchingSchema)) matchingSchema.description = '';
        formData.schema_model_uuid = matchingSchema.uuid;
        formData.selectedSchema = matchingSchema;
        selectedSchemaType.value = matchingSchema.type || null;
      } else {
        // 🚨 Schéma manquant détecté !
        console.warn('Schéma manquant détecté:', initialMemo.schema_model.uuid);
        missingSchemaInfo.value = {
          originalUuid: initialMemo.schema_model.uuid,
          originalType: initialMemo.schema_model.type || 'Inconnu',
          originalName: initialMemo.schema_model.appellation || 'Schéma sans nom',
          memoTitle: initialMemo.title || 'MEMoS sans titre'
        };
        showSchemaRecoveryModal.value = true;
        return; // Arrêter le chargement jusqu'à ce que l'utilisateur choisisse un remplacement
      }

      // 🚨 CORRECTION : Mettre à jour le user_id avec le vrai propriétaire du MEMoS
      formData.user_id = initialMemo.user_id;

      //CHARGEMENT DES ASSETS

      //WHAT:
      if (initialMemo.material_entity) {
        const materialEntityWithLocation = {
          ...initialMemo.material_entity,
          provenance_location: initialMemo.material_entity.provenance_location ? {
            ...initialMemo.material_entity.provenance_location,
            user_id: formData.user_id,
            is_public: true
          } : null
        };
        formData.materialEntity = materialEntityWithLocation;
        formData.material_entity_uuid = initialMemo.material_entity.uuid;
      }
      if (initialMemo.digital_entity_output) {
        formData.digitalEntityOutput = initialMemo.digital_entity_output;
        formData.digital_entity_output_id = initialMemo.digital_entity_output.uuid;
        formData.NewDigitalEntityCreated = true;
      }

      if (initialMemo.digital_entity_input) {
        formData.digitalEntityInput = initialMemo.digital_entity_input;
        formData.digital_entity_input_id = initialMemo.digital_entity_input.uuid;

      }

      //WHY:

      if (initialMemo.project) {
        Object.assign(formData, {
          project: initialMemo.project,
          project_uuid: initialMemo.project.uuid
        });
      }
      formData.instrumentation_description = initialMemo.instrumentation_description;
      formData.instrumentation_link = initialMemo.instrumentation_link;

      //WHO:

      formData.selectedAgents = initialMemo.agents_entries?.map(entry => ({
        ...entry.agent,
        rolesList: entry.roleslist || ''
      })) || [];

      //WHERE:
      if (initialMemo.location) {
        Object.assign(formData, {
          location: initialMemo.location,
          location_uuid: initialMemo.location.uuid
        });
      }
      //WHEN:
      formData.start_time = initialMemo.start_time;
      formData.end_time = initialMemo.end_time;
      formData.time_zone = initialMemo.time_zone;
      formData.time_link = initialMemo.time_link || '';

      //Autres initialisations:
      formData.is_public = initialMemo.is_public;
      formData.is_draft = initialMemo.is_draft;
      formData.is_saved = false;
      formData.data_entries = initialMemo.data_entries || [];


      if (currentMode.value === 'useModel') {
        const newUuid = generateUUID();
        formData.uuid = newUuid;
        await generateTriid().then(triid => {
          formData.triid = triid;
        });
        formData.is_model = false;
        formData.is_draft = true;
        formData.title = `${initialMemo.title || ''} (copie)`;
        formData.is_saved = false;
        formData.material_entity_uuid = '';
        formData.digital_entity_input_id = '';
        formData.digital_entity_output_id = '';
        formData.NewDigitalEntityCreated = false;
        // 🚨 CORRECTION : Pour useModel, remettre l'utilisateur connecté comme propriétaire
        formData.user_id = authStore.user?.uuid || '';
      } 
      else if (currentMode.value === 'edit') {
        formData.is_model = initialMemo.is_model;
        formData.uuid = initialMemo.uuid;
        formData.triid = initialMemo.triid;
        formData.title = initialMemo.title;
      }

      // Force la mise à jour des composants enfants
      forceProjectLocationReactivity();
      steps.value[0].visited = true;
      steps.value[0].isComplete = !!formData.schema_model_uuid;

      steps.value[1].visited = true;
      steps.value[1].isComplete = !!formData.material_entity_uuid && !!formData.digital_entity_output_id;

      steps.value[2].visited = true;
      steps.value[2].isComplete = !!formData.project;

      steps.value[3].visited = true;
      steps.value[3].isComplete = formData.selectedAgents && formData.selectedAgents.length > 0;

      steps.value[4].visited = true;
      steps.value[4].isComplete = !!formData.location_uuid;

      steps.value[5].visited = true;
      steps.value[5].isComplete = !!formData.start_time;

      steps.value[6].visited = true;
      steps.value[6].isComplete = formData.data_entries && formData.data_entries.length > 0;

      if (notification.value) {
        notification.value.showNotification(
          currentMode.value === 'useModel'
            ? 'Modèle chargé avec succès'
            : 'MEMoS chargé avec succès',
          'success'
        );
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement du MEMoS:', error);
    if (notification.value) {
      notification.value.showNotification(
        'Erreur lors du chargement du MEMoS',
        'error'
      );
    }
  }
};

// Fonction pour forcer la réactivité des propriétés project et location
const forceProjectLocationReactivity = (): void => {
  nextTick(() => {
    if (formData.project) {
      formData.project = { ...formData.project };
    }
    if (formData.location) {
      formData.location = { ...formData.location };
    }
  });
};

// Détecter les changements dans le formulaire
const detectChanges = (): void => {
  hasChanges.value = true;
};

// Sauvegarder lors du changement d'étape si nécessaire
const handleStepChange = async (newStep: number): Promise<void> => {
  if (hasChanges.value && formData.is_saved == true) {
    await saveDraft();
    hasChanges.value = false;
  }
  currentStep.value = newStep;
};

// Ajouter un watcher sur formData pour détecter les changements
watch(() => formData, () => {
  detectChanges();
}, { deep: true });

// Watcher pour recharger le mémo à chaque changement de memoId dans l'URL
watch(targetMemoId, async (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    await loadInitialMemo();
  }
});

// Gère le clic sur le bouton de sauvegarde
const handleSaveClick = (): void => {
  if (isReadOnlyMode.value) {
    return;
  }
  // Appel de la fonction de sauvegarde unifiée
  saveDraft();
};

// Sauvegarde le brouillon ou la version finale du MEMoS
const saveDraft = async (): Promise<void> => {
  if (isSaving.value) return;

  // Validation : vérifier que l'entité matérielle est définie si ce n'est pas un brouillon NI un modèle
  if (!formData.is_draft && !formData.is_model && !formData.material_entity_uuid) {
    showNotification("Impossible de sauvegarder : un mémo non-modèle doit obligatoirement être rattaché à une entité matérielle. Merci de sélectionner ou créer une entité matérielle avant de poursuivre.", "error");
    return;
  }

  isSaving.value = true;
  isLoading.value = true;

  try {
    // 🚨 CORRECTION FINALE : Récupérer l'ID utilisateur directement depuis le store
    const currentUserId = authStore.user?.uuid;
    if (!currentUserId) {
      // Afficher une erreur claire et arrêter le processus
      showNotification("Session utilisateur invalide. Veuillez vous reconnecter.", "error");
      isSaving.value = false;
      isLoading.value = false;
      // Rediriger vers la page de connexion pourrait être une bonne UX ici
      // router.push('/auth');
      return;
    }
    const triid = formData.triid || await generateTriid();
    // Le payload complet et correct
    const payload: MemoCreate = {
      user_id: currentUserId, // Utilisation de l'ID frais et validé
      uuid: formData.uuid || generateUUID(),
      title: formData.title,
      triid,
      schema_model_uuid: formData.schema_model_uuid,
      material_entity_uuid: formData.material_entity_uuid || undefined,
      is_public: formData.is_public,
      is_model: formData.is_model,
      is_draft: formData.is_draft,
      data_entries: formData.data_entries,
      agent_entries: (formData.selectedAgents || []).map((agent: AgentWithRoles) => ({
        agent_id: agent.uuid,
        roleslist: agent.rolesList || '' // Correction de la faute de frappe
      })),
      digital_entity_input_id: formData.digital_entity_input_id || undefined,
      digital_entity_output_id: formData.digital_entity_output_id || undefined,
      location_uuid: formData.location_uuid || undefined,
      project_uuid: formData.project_uuid || undefined,
      instrumentation_description: formData.instrumentation_description || undefined,
      instrumentation_link: formData.instrumentation_link || undefined,
      start_time: formData.start_time || undefined,
      end_time: formData.end_time || undefined,
      time_zone: formData.time_zone || undefined,
      time_link: formData.time_link || undefined,
    };

    console.log('🔍 DIAGNOSTIC SAUVEGARDE:', payload);

    let savedMemo;
    // La condition pour mettre à jour est que le mémo ait déjà été sauvegardé
    // ou que nous soyons explicitement en mode édition OU qu'il s'agisse d'un import existant.
    if (formData.is_saved || currentMode.value === 'edit' || isExistingImport.value) {
      savedMemo = await updateMemo(formData.uuid || generateUUID(), payload);
      assetsCacheStore.updateAssetInCache('memos', savedMemo); // <-- MAJ cache après update
      showNotification("Brouillon mis à jour avec succès !", "success");
    } else {
      savedMemo = await createMemo(payload);
      assetsCacheStore.addAssetToCache('memos', savedMemo); // <-- MAJ cache après création
      showNotification("Brouillon créé avec succès !", "success");
      await changeMode('edit'); // Passer en mode édition après la première sauvegarde
      formData.is_saved = true; // Marquer comme sauvegardé
    }

    console.log("Mémo sauvegardé : ", savedMemo);
    hasChanges.value = false;

  } catch (error) {
    console.error("Form - Erreur détaillée lors de la sauvegarde:", error);
    // Gestion spécifique de l'erreur de validation sur material_entity_uuid
    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      if (error.message.includes('material_entity_uuid') && error.message.includes('obligatoire')) {
        showNotification("Impossible de sauvegarder : un mémo non-modèle doit obligatoirement être rattaché à une entité matérielle. Merci de sélectionner ou créer une entité matérielle avant de poursuivre.", "error");
      } else {
        showNotification(error.message, "error");
      }
    } else {
      showNotification("Erreur lors de la sauvegarde du brouillon.", "error");
    }
    formData.is_saved = false;
  } finally {
    isSaving.value = false;
    isLoading.value = false;
  }
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};

const toggleModelMode = (): void => {
  formData.is_model = !formData.is_model;
  updateFormData({ is_model: formData.is_model });
  updateStepCompletion(formData.is_model);
};

const toggleDraftMode = (): void => {
  // Si le mode modèle est actif, on ne fait rien
  if (formData.is_model) {
    showNotification("Le mode brouillon n'est pas disponible en mode modèle.", "warning");
    return;
  }

  // Si on essaie d'activer le mode brouillon (is_draft = true)
  if (formData.is_draft) {
    // Vérifier si l'étape WHAT est complète
    if (!steps.value[1].isComplete) {
      showNotification("Vous ne pouvez pas activer le mode brouillon tant que l'étape WHAT n'est pas complétée.", "warning");
      return;
    }
  }
  formData.is_draft = !formData.is_draft;
};

const togglePublicMode = (): void => {
  formData.is_public = !formData.is_public;
  updateFormData({ is_public: formData.is_public });
};

// Gestionnaires pour le modal de récupération de schéma
const handleSchemaReplaced = async (newSchema: SchemaWithSectionList): Promise<void> => {
  console.log('Remplacement de schéma:', newSchema);

  // Mettre à jour le formData avec le nouveau schéma
  formData.schema_model_uuid = newSchema.uuid;
  formData.selectedSchema = {
    uuid: newSchema.uuid,
    section_list: newSchema.section_list,
    type: newSchema.type,
    appellation: newSchema.appellation
  };
  selectedSchemaType.value = newSchema.type || null;

  // Marquer l'étape SchemaStep comme complète
  steps.value[0].visited = true;
  steps.value[0].isComplete = true;

  // Continuer le chargement du MEMoS avec le nouveau schéma
  if (targetMemoId.value) {
    await continueLoadingMemoWithNewSchema();
  }

  // Notification de succès
  if (notification.value) {
    notification.value.showNotification(
      `Schéma remplacé par "${newSchema.appellation}"`,
      'success'
    );
  }
};

const handleSchemaRecoveryCancel = (): void => {
  console.log('Récupération de schéma annulée');
  showSchemaRecoveryModal.value = false;
  missingSchemaInfo.value = null;

  // Notification d'information
  if (notification.value) {
    notification.value.showNotification(
      'Chargement du MEMoS annulé',
      'info'
    );
  }
};

const continueLoadingMemoWithNewSchema = async (): Promise<void> => {
  if (!targetMemoId.value) return;

  try {
    const response = await memos.fetchMemosUuid(targetMemoId.value);
    if (response && response.length > 0) {
      const initialMemo = response[0];

      // Continuer le chargement des autres données du MEMoS
      // (le schéma a déjà été défini dans handleSchemaReplaced)

      //CHARGEMENT DES ASSETS

      //WHAT:
      if (initialMemo.material_entity) {
        const materialEntityWithLocation = {
          ...initialMemo.material_entity,
          provenance_location: initialMemo.material_entity.provenance_location ? {
            ...initialMemo.material_entity.provenance_location,
            user_id: formData.user_id,
            is_public: true
          } : null
        };
        formData.materialEntity = materialEntityWithLocation;
        formData.material_entity_uuid = initialMemo.material_entity.uuid;
      }
      if (initialMemo.digital_entity_output) {
        formData.digitalEntityOutput = initialMemo.digital_entity_output;
        formData.digital_entity_output_id = initialMemo.digital_entity_output.uuid;
        formData.NewDigitalEntityCreated = true;
      }

      if (initialMemo.digital_entity_input) {
        formData.digitalEntityInput = initialMemo.digital_entity_input;
        formData.digital_entity_input_id = initialMemo.digital_entity_input.uuid;
      }

      //WHY:
      if (initialMemo.project) {
        Object.assign(formData, {
          project: initialMemo.project,
          project_uuid: initialMemo.project.uuid
        });
      }
      formData.instrumentation_description = initialMemo.instrumentation_description;
      formData.instrumentation_link = initialMemo.instrumentation_link;

      //WHO:
      formData.selectedAgents = initialMemo.agents_entries?.map(entry => ({
        ...entry.agent,
        rolesList: entry.roleslist || ''
      })) || [];

      //WHERE:
      if (initialMemo.location) {
        Object.assign(formData, {
          location: initialMemo.location,
          location_uuid: initialMemo.location.uuid
        });
      }
      //WHEN:
      formData.start_time = initialMemo.start_time;
      formData.end_time = initialMemo.end_time;
      formData.time_zone = initialMemo.time_zone;
      formData.time_link = initialMemo.time_link || '';

      //Autres initialisations:
      formData.is_public = initialMemo.is_public;
      formData.is_draft = initialMemo.is_draft;
      formData.is_saved = false;

      // Marquer les data_entries incompatibles avec le nouveau schéma
      const processedDataEntries = (initialMemo.data_entries || []).map((entry: MemoDataEntry) => ({
        ...entry,
        isIncompatible: !formData.selectedSchema?.section_list?.includes(entry.section_id),
        originalSchemaUuid: missingSchemaInfo.value?.originalUuid
      }));

      formData.data_entries = processedDataEntries;

      // Compter les données incompatibles
      const incompatibleCount = processedDataEntries.filter((entry: MemoDataEntry & { isIncompatible?: boolean }) => entry.isIncompatible).length;
      if (incompatibleCount > 0 && notification.value) {
        notification.value.showNotification(
          `${incompatibleCount} données ne correspondent plus au nouveau schéma mais sont conservées`,
          'warning'
        );
      }

      if (currentMode.value === 'useModel') {
        const newUuid = generateUUID();
        formData.uuid = newUuid;
        await generateTriid().then(triid => {
          formData.triid = triid;
        });
        formData.is_model = false;
        formData.is_draft = true;
        formData.title = `${initialMemo.title || ''} (copie)`;
        formData.is_saved = false;
        formData.material_entity_uuid = '';
        formData.digital_entity_input_id = '';
        formData.digital_entity_output_id = '';
        formData.NewDigitalEntityCreated = false;
      } else {
        formData.uuid = initialMemo.uuid;
        console.log('DEBUG UPDATE:', {
          uuid_url: formData.uuid,
          uuid_payload: initialMemo.uuid,
        });
        formData.triid = initialMemo.triid;
        formData.title = initialMemo.title;
      }

      // Mettre à jour l'état des étapes
      steps.value[1].visited = true;
      steps.value[1].isComplete = !!formData.material_entity_uuid && !!formData.digital_entity_output_id;

      steps.value[2].visited = true;
      steps.value[2].isComplete = !!formData.project;

      steps.value[3].visited = true;
      steps.value[3].isComplete = formData.selectedAgents && formData.selectedAgents.length > 0;

      steps.value[4].visited = true;
      steps.value[4].isComplete = !!formData.location_uuid;

      steps.value[5].visited = true;
      steps.value[5].isComplete = !!formData.start_time;

      steps.value[6].visited = true;
      steps.value[6].isComplete = formData.data_entries && formData.data_entries.length > 0;

      // Force la mise à jour des composants enfants
      forceProjectLocationReactivity();
    }
  } catch (error) {
    console.error('Erreur lors du chargement du MEMoS avec nouveau schéma:', error);
    if (notification.value) {
      notification.value.showNotification(
        'Erreur lors du chargement du MEMoS',
        'error'
      );
    }
  }
};

// Development helpers - uncomment for debugging
// watchEffect(() => {
//   console.log('Form.vue - currentStep:', currentStep.value);
//   console.log('Form.vue - steps:', steps);
// });

</script>

<style scoped>
input,
textarea {
  margin-bottom: 0px;
}
</style>